import { useEffect, useRef, type CSSProperties, type KeyboardEvent, type ReactNode } from "react";
import { LABEL, PANEL, ROW, ROW_ON } from "./theme";

// One list for every pick on the lobby (#225 round 2): skater, ride and map.
// A name per row, the picked one filled with the accent, what it is beside
// the list, built to hold dozens of entries: the list scrolls and keeps the
// picked row in view. ↑ ↓ (and the stick or d-pad, through the lobby's pad
// nav) move through it; a tap or click picks.

export interface PickItem {
  id: string;
  name: string;
  /** Something small before the name: a badge, an icon, a thumbnail. */
  lead?: ReactNode;
  /** A longer line for the row's tooltip. */
  title?: string;
}

/** The picked item's id, or the first one's when it isn't in the list. */
function pickedIndex(items: readonly PickItem[], picked: string): number {
  return Math.max(0, items.findIndex((it) => it.id === picked));
}

/** How tall a list showing `rows` rows is. */
function listHeight(count: number, rows: number): string {
  return `${rows * 2.5 + (count > rows ? 1.25 : 0)}rem`;
}

/** The row button for `id` in a list. */
function rowOf(ul: HTMLElement | null, id: string | undefined): HTMLElement | undefined {
  return [...(ul?.querySelectorAll<HTMLElement>("[data-pick-row]") ?? [])].find((b) => b.dataset.pickRow === id);
}

/** The next id ↑ (-1) or ↓ (+1) from `picked`, stopping at the ends. */
export function stepPick(items: readonly PickItem[], picked: string, by: -1 | 1): string {
  const i = Math.min(items.length - 1, Math.max(0, pickedIndex(items, picked) + by));
  return items[i]?.id ?? picked;
}

/**
 * A labelled panel: the list on one side and `aside` (the picked one's
 * card, picture or stats) on the other, or under it when `stack` is set.
 * `rows` is how many rows show before it scrolls.
 */
export function PickList({
  label,
  items,
  picked,
  onPick,
  aside,
  rows = 5,
  stack = false,
  action,
  ...data
}: {
  label: string;
  items: readonly PickItem[];
  picked: string;
  onPick: (id: string) => void;
  aside?: ReactNode;
  rows?: number;
  stack?: boolean;
  /** A button under the panel's list and aside (the park's Skate). */
  action?: ReactNode;
  [data: `data-${string}`]: boolean;
}) {
  const list = useRef<HTMLUListElement>(null);
  const current = items[pickedIndex(items, picked)]?.id;
  // Keep the picked row in view, inside the list only (never scrolling the lobby).
  useEffect(() => {
    const ul = list.current;
    const row = rowOf(ul, current);
    if (!ul || !row) return;
    const top = row.offsetTop - ul.offsetTop;
    if (top < ul.scrollTop) ul.scrollTop = top;
    else if (top + row.offsetHeight > ul.scrollTop + ul.clientHeight) ul.scrollTop = top + row.offsetHeight - ul.clientHeight;
  }, [current]);
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
    e.preventDefault();
    const next = stepPick(items, picked, e.key === "ArrowUp" ? -1 : 1);
    onPick(next);
    rowOf(list.current, next)?.focus();
  };
  return (
    <div role="group" aria-label={label} {...data} className={`${PANEL} flex w-full flex-col gap-2 p-3`}>
      <span className={LABEL}>{label}</span>
      <div className={`flex gap-3 ${stack ? "flex-col" : "items-start"}`}>
        <ul
          ref={list}
          onKeyDown={onKeyDown}
          data-pick-list
          // A row is 2.25rem plus the gap; the half row past the last whole one says there's more. A phone on its side shows three.
          style={{ "--list-h": listHeight(items.length, rows), "--list-h-short": listHeight(items.length, Math.min(rows, 3)) } as CSSProperties}
          className={`relative max-h-(--list-h) [@media(max-height:520px)]:max-h-(--list-h-short) flex min-w-0 flex-col gap-1 overflow-y-auto overscroll-contain pr-1 [scrollbar-color:var(--color-accent)_transparent] [scrollbar-width:thin] ${stack ? "w-full" : "flex-1"}`}
        >
          {items.map((it) => {
            const on = it.id === current;
            return (
              <li key={it.id} className="shrink-0">
                <button
                  type="button"
                  data-pick-row={it.id}
                  aria-pressed={on}
                  aria-label={it.name}
                  title={it.title}
                  onClick={() => onPick(it.id)}
                  className={`flex h-9 w-full items-center gap-2 rounded-md border-2 px-2 text-left focus-visible:outline-3 focus-visible:outline-offset-1 focus-visible:outline-focus ${
                    on ? ROW_ON : `${ROW} hover:border-accent`
                  }`}
                >
                  {it.lead}
                  <span className="min-w-0 flex-1 truncate text-sm font-black uppercase italic leading-none">{it.name}</span>
                </button>
              </li>
            );
          })}
        </ul>
        {aside && <div className={`min-w-0 ${stack ? "w-full" : "w-[45%] shrink-0 [@media(max-height:520px)]:w-[35%]"}`}>{aside}</div>}
      </div>
      {action}
    </div>
  );
}
